import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getStoreOrders,
  getStoreApiPayload,
  isStoreApiSuccess,
  patchStoreOrder,
  refundStoreOrder,
} from '../../services/storeApi';
import {
  formatAddressLines,
  formatAddressShort,
  formatDateTime,
  formatInr,
  formatOrderCustomerLabel,
  FULFILLMENT_STATUS_LABELS,
  getOrderAddress,
  getOrderCustomerPhone,
  getOrderProduct,
  getOrderProductTitle,
  ORDER_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  PRODUCT_TYPE_LABELS,
} from '../Store/storeLabels';
import { useStoreSession } from '../Store/useStoreSession';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const PAGE_SIZE = 50;

const TAB_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'merch_queue', label: 'Merch queue' },
  { key: 'processing', label: 'Processing gift cards' },
  { key: 'failed', label: 'Failed / refunded' },
  { key: 'completed', label: 'Completed' },
];

function tabIndexFromKey(key) {
  const idx = TAB_FILTERS.findIndex((t) => t.key === key);
  return idx >= 0 ? idx : 0;
}

function filterOrdersByTab(orders, tabKey) {
  switch (tabKey) {
    case 'merch_queue':
      return orders.filter(
        (o) => o.product_type === 'DUGOUT_PRODUCT' && o.fulfillment_status === 'MANUAL_REQUIRED'
      );
    case 'processing':
      return orders.filter((o) => o.status === 'PROCESSING');
    case 'failed':
      return orders.filter((o) => ['FAILED', 'REFUNDED'].includes(o.status));
    case 'completed':
      return orders.filter((o) => o.status === 'SUCCESS');
    default:
      return orders;
  }
}

function StoreOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = tabIndexFromKey(searchParams.get('tab') || 'all');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [apiStatus, setApiStatus] = useState('');
  const [apiProductType, setApiProductType] = useState('');
  const [offset, setOffset] = useState(0);
  const [detailOrder, setDetailOrder] = useState(null);
  const [fulfillmentStatus, setFulfillmentStatus] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { showSnackbar, handleSessionExpired } = useStoreSession(setSnackbar);
  const tabKey = TAB_FILTERS[tab]?.key || 'all';

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStoreOrders({
        limit: PAGE_SIZE,
        offset,
        status: apiStatus || undefined,
        productType: apiProductType || undefined,
      });
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load orders', 'error');
        setOrders([]);
        return;
      }
      const payload = getStoreApiPayload(res);
      setOrders(payload?.orders || []);
    } finally {
      setLoading(false);
    }
  }, [offset, apiStatus, apiProductType, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const key = searchParams.get('tab');
    if (key) setTab(tabIndexFromKey(key));
  }, [searchParams]);

  useEffect(() => {
    const key = TAB_FILTERS[tab]?.key;
    if (key) setSearchParams({ tab: key }, { replace: true });
  }, [tab, setSearchParams]);

  const displayedOrders = useMemo(() => filterOrdersByTab(orders, tabKey), [orders, tabKey]);

  const openDetail = (order) => {
    setDetailOrder(order);
    setFulfillmentStatus(order.fulfillment_status || '');
    setOrderStatus(order.status || '');
    setAdminNotes(order.admin_notes || '');
  };

  const patchOrderFields = async (orderId, body, successMessage) => {
    setSaving(true);
    try {
      const res = await patchStoreOrder(orderId, body);
      if (handleSessionExpired(res)) return false;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Update failed', 'error');
        return false;
      }
      showSnackbar(successMessage);
      setDetailOrder(null);
      loadOrders();
      return true;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOrder = async () => {
    if (!detailOrder) return;
    const body = {};
    if (fulfillmentStatus) body.fulfillment_status = fulfillmentStatus;
    if (orderStatus) body.status = orderStatus;
    if (adminNotes.trim()) body.admin_notes = adminNotes.trim();
    await patchOrderFields(detailOrder.id, body, 'Order updated');
  };

  const handleMarkInProgress = async (order) => {
    const target = order || detailOrder;
    if (!target) return;
    await patchOrderFields(
      target.id,
      {
        fulfillment_status: 'IN_PROGRESS',
        admin_notes: adminNotes.trim() || 'Marked in progress from admin panel',
      },
      'Marked in progress'
    );
  };

  const handleMarkDelivered = async (order) => {
    const target = order || detailOrder;
    if (!target) return;
    await patchOrderFields(
      target.id,
      {
        fulfillment_status: 'DELIVERED',
        status: 'FULFILLED',
        admin_notes: adminNotes.trim() || 'Delivered from admin panel',
      },
      'Marked delivered'
    );
  };

  const handleRefund = async () => {
    if (!detailOrder) return;
    if (!window.confirm('Refund Fan Energy to the user? This is idempotent if already refunded.')) return;

    setSaving(true);
    try {
      const res = await refundStoreOrder(detailOrder.id, {
        admin_notes: adminNotes.trim() || 'Refunded from admin panel',
      });
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Refund failed', 'error');
        return;
      }
      showSnackbar('Order refunded');
      setDetailOrder(null);
      loadOrders();
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'SUCCESS' || status === 'FULFILLED') return 'success';
    if (['FAILED', 'REFUNDED', 'CANCELLED'].includes(status)) return 'error';
    if (status === 'PROCESSING') return 'warning';
    return 'default';
  };

  const detailAddress = detailOrder ? getOrderAddress(detailOrder) : null;
  const isMerchOrder = detailOrder?.product_type === 'DUGOUT_PRODUCT';

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Store Orders</h3>
                  <Typography variant="body2" color="text.secondary">
                    Merch fulfillment, gift card processing, refunds
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/admin/store" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Store dashboard
                      </Button>
                      <TextField
                        select
                        size="small"
                        label="API status"
                        value={apiStatus}
                        onChange={(e) => {
                          setApiStatus(e.target.value);
                          setOffset(0);
                        }}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="">Any</MenuItem>
                        {Object.keys(ORDER_STATUS_LABELS).map((s) => (
                          <MenuItem key={s} value={s}>
                            {ORDER_STATUS_LABELS[s]}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Product type"
                        value={apiProductType}
                        onChange={(e) => {
                          setApiProductType(e.target.value);
                          setOffset(0);
                        }}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">Any</MenuItem>
                        <MenuItem value="HUBBLE_GIFT_CARD">Hubble gift card</MenuItem>
                        <MenuItem value="DUGOUT_PRODUCT">Dugout merch</MenuItem>
                      </TextField>
                      <Button variant="text" onClick={loadOrders} disabled={loading}>
                        Refresh
                      </Button>
                    </Box>

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                      {TAB_FILTERS.map((t, i) => (
                        <Tab key={t.key} label={t.label} value={i} />
                      ))}
                    </Tabs>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Showing {displayedOrders.length} of {orders.length} loaded (tab filter is client-side;
                          fulfillment_status is not an API query param yet)
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Order</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment</TableCell>
                                <TableCell>Fulfillment</TableCell>
                                <TableCell>FE</TableCell>
                                <TableCell>Ship to</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {displayedOrders.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={11} align="center">
                                    No orders
                                  </TableCell>
                                </TableRow>
                              ) : (
                                displayedOrders.map((o) => {
                                  const addr = getOrderAddress(o);
                                  const product = getOrderProduct(o);
                                  const phone = getOrderCustomerPhone(o);
                                  const merchNeedsAction =
                                    o.product_type === 'DUGOUT_PRODUCT' && o.fulfillment_status === 'MANUAL_REQUIRED';
                                  return (
                                    <TableRow key={o.id} hover selected={detailOrder?.id === o.id}>
                                      <TableCell>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                          {o.id.slice(0, 8)}…
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatDateTime(o.created_at)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                          {formatOrderCustomerLabel(o)}
                                        </Typography>
                                        {phone && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {phone}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                          {getOrderProductTitle(o)}
                                        </Typography>
                                        {product?.category_key && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {product.category_key}
                                            {product.product_subtype ? ` · ${product.product_subtype}` : ''}
                                          </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {o.fan_energy_amount} FE
                                          {product?.original_price_inr
                                            ? ` · MRP ${formatInr(product.original_price_inr)}`
                                            : ''}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{PRODUCT_TYPE_LABELS[o.product_type] || o.product_type}</TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={ORDER_STATUS_LABELS[o.status] || o.status}
                                          color={statusColor(o.status)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={PAYMENT_STATUS_LABELS[o.payment_status] || o.payment_status || '—'}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={FULFILLMENT_STATUS_LABELS[o.fulfillment_status] || o.fulfillment_status}
                                          color={merchNeedsAction ? 'warning' : 'default'}
                                        />
                                      </TableCell>
                                      <TableCell>{o.fan_energy_amount}</TableCell>
                                      <TableCell sx={{ maxWidth: 220 }}>
                                        <Typography variant="caption" display="block">
                                          {formatAddressShort(addr)}
                                        </Typography>
                                        {addr?.pin_code && (
                                          <Typography variant="caption" color="text.secondary">
                                            PIN {addr.pin_code}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>{formatDateTime(o.updated_at)}</TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                          {merchNeedsAction && (
                                            <Button size="small" onClick={() => handleMarkInProgress(o)} disabled={saving}>
                                              In progress
                                            </Button>
                                          )}
                                          {o.product_type === 'DUGOUT_PRODUCT' &&
                                            ['MANUAL_REQUIRED', 'IN_PROGRESS'].includes(o.fulfillment_status) && (
                                              <Button size="small" color="success" onClick={() => handleMarkDelivered(o)} disabled={saving}>
                                                Delivered
                                              </Button>
                                            )}
                                          <Button size="small" onClick={() => openDetail(o)}>
                                            Details
                                          </Button>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                            Previous
                          </Button>
                          <Button disabled={orders.length < PAGE_SIZE} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                            Next
                          </Button>
                        </Box>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={!!detailOrder} onClose={() => setDetailOrder(null)} maxWidth="md" fullWidth>
          <DialogTitle>Order {detailOrder?.id}</DialogTitle>
          <DialogContent>
            {detailOrder && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <GridLikeDetails order={detailOrder} />

                {detailOrder.failure_reason && (
                  <Alert severity="error">Failure: {detailOrder.failure_reason}</Alert>
                )}

                {isMerchOrder && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Shipping address
                    </Typography>
                    {detailOrder.user_store_address_id != null && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        From saved address book · ID {detailOrder.user_store_address_id}
                      </Typography>
                    )}
                    {detailAddress ? (
                      formatAddressLines(detailAddress).map((line) => (
                        <Typography key={line} variant="body2">
                          {line}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No shipping_address on this order.
                      </Typography>
                    )}
                  </Paper>
                )}

                <Divider />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {isMerchOrder && detailOrder.fulfillment_status === 'MANUAL_REQUIRED' && (
                    <Button variant="outlined" onClick={() => handleMarkInProgress()} disabled={saving}>
                      Mark in progress
                    </Button>
                  )}
                  {isMerchOrder && ['MANUAL_REQUIRED', 'IN_PROGRESS'].includes(detailOrder.fulfillment_status) && (
                    <Button variant="contained" color="success" onClick={() => handleMarkDelivered()} disabled={saving}>
                      Mark delivered
                    </Button>
                  )}
                </Box>

                <TextField
                  select
                  label="Fulfillment status"
                  fullWidth
                  value={fulfillmentStatus}
                  onChange={(e) => setFulfillmentStatus(e.target.value)}
                >
                  {Object.entries(FULFILLMENT_STATUS_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Order status"
                  fullWidth
                  value={orderStatus}
                  onChange={(e) => setOrderStatus(e.target.value)}
                >
                  {Object.entries(ORDER_STATUS_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Admin notes"
                  multiline
                  minRows={3}
                  fullWidth
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={handleRefund} disabled={saving || detailOrder?.status === 'REFUNDED'}>
              Refund
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setDetailOrder(null)}>Close</Button>
            <Button variant="contained" onClick={handleSaveOrder} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

function GridLikeDetails({ order }) {
  const addr = getOrderAddress(order);
  const product = getOrderProduct(order);
  const rows = [
    ['Customer', formatOrderCustomerLabel(order)],
    ['Phone', getOrderCustomerPhone(order) || '—'],
    ['User ID', order.user_id],
    ['Product', getOrderProductTitle(order)],
    ['Product ID', order.store_product_id],
    ['Type', PRODUCT_TYPE_LABELS[order.product_type] || order.product_type],
    ['Fan energy', order.fan_energy_amount],
    ['INR face', formatInr(order.real_amount_inr)],
    ['MRP', product?.original_price_inr != null ? formatInr(product.original_price_inr) : '—'],
    ['Quantity', order.quantity],
    ['Payment', PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status],
    ['Order status', ORDER_STATUS_LABELS[order.status] || order.status],
    ['Fulfillment', FULFILLMENT_STATUS_LABELS[order.fulfillment_status] || order.fulfillment_status],
    ['Saved address ID', order.user_store_address_id ?? '—'],
    ['Hubble order', order.hubble_order_id || '—'],
    ['Hubble ref', order.hubble_reference_id || '—'],
    ['Idempotency', order.idempotency_key || '—'],
    ['Created', formatDateTime(order.created_at)],
    ['Updated', formatDateTime(order.updated_at)],
  ];

  const redemption = product?.metadata?.redemption_details;
  const deliveryDays = product?.metadata?.estimated_delivery_days;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {product?.image_url && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box
            component="img"
            src={product.image_url}
            alt={product.title || 'Product'}
            sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee' }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {product.title}
            </Typography>
            {product.description && (
              <Typography variant="body2" color="text.secondary">
                {product.description}
              </Typography>
            )}
            {product.category_key && (
              <Typography variant="caption" color="text.secondary">
                {product.category_key}
                {product.product_subtype ? ` · ${product.product_subtype}` : ''}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        {rows.map(([label, value]) => (
          <Typography key={label} variant="body2">
            <strong>{label}:</strong> {value}
          </Typography>
        ))}
      </Box>

      {(deliveryDays != null || (Array.isArray(redemption) && redemption.length > 0)) && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {deliveryDays != null && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Est. delivery:</strong> {deliveryDays} days
            </Typography>
          )}
          {Array.isArray(redemption) && redemption.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Redemption details
              </Typography>
              {redemption.map((line) => (
                <Typography key={line} variant="body2" color="text.secondary">
                  · {line}
                </Typography>
              ))}
            </>
          )}
        </Paper>
      )}

      {order.admin_notes && (
        <Typography variant="body2">
          <strong>Admin notes:</strong> {order.admin_notes}
        </Typography>
      )}
      {addr && order.product_type !== 'DUGOUT_PRODUCT' && (
        <Typography variant="body2">
          <strong>Address:</strong> {formatAddressShort(addr)}
        </Typography>
      )}
    </Box>
  );
}

export default StoreOrders;
